import axiosInstance from '../lib/axios';
import {
  Candidate,
  CandidateFilters,
  CandidatesResponse,
  CreateCandidateRequest,
  UpdateCandidateRequest,
  CandidateStats,
  TimelineEntry,
  HiringStatus
} from '../types/models';
import { timelineService } from './timeline.service';

export const CANDIDATE_STATUS_OPTIONS = Object.values(HiringStatus);

export const CANDIDATE_STATUSES = HiringStatus;

class CandidateService {
  private baseUrl = '/candidates';

  // GET all candidates with filters
  async getCandidates(filters: CandidateFilters = {}): Promise<CandidatesResponse> {
    try {

      const params = new URLSearchParams();

      // Only add valid, non-empty filter parameters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '' && value !== 'all') {
          // Convert numbers to strings for URL params
          const stringValue = typeof value === 'number' ? value.toString() : value;
          if (stringValue && stringValue.trim() !== '') {
            params.append(key, stringValue);
          }
        }
      });

      const queryString = params.toString();
      const url = `${this.baseUrl}${queryString ? `?${queryString}` : ''}`;



      const response = await axiosInstance.get<CandidatesResponse>(url);


      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // GET candidate by ID
  async getCandidateById(id: string): Promise<Candidate> {
    try {
      const response = await axiosInstance.get<any>(`${this.baseUrl}/${id}`);
      // Handle both wrapped { data: Candidate } and direct Candidate object
      if (response.data && response.data.data) {
        return response.data.data;
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // POST create candidate
  async createCandidate(candidateData: CreateCandidateRequest & { resumeFile?: File }): Promise<Candidate> {
    try {
      // Validate new fields
      this.validateCandidateData(candidateData);

      // Check if we have a resume file to upload
      const { resumeFile, ...candidateDataWithoutFile } = candidateData;

      // Clean up empty strings to avoid backend validation issues
      const cleanedData: any = { ...candidateDataWithoutFile };
      // Fields that should allow being cleared/empty strings
      const clearableFields = [
        'expectedSalary',
        'currentSalary',
        'currentCompany',
        'currentRole',
        'noticePeriod',
        'linkedinUrl',
        'notes',
        'reasonForLeavingPreviousRole',
        'communicationSkills'
      ];

      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === '' && !clearableFields.includes(key)) {
          cleanedData[key] = undefined;
        }
      });

      if (resumeFile) {
        // Create FormData for file upload
        const formData = new FormData();

        // Add the resume file
        formData.append('resume', resumeFile);

        // Add fields from cleanedData
        Object.keys(cleanedData).forEach(key => {
          if (cleanedData[key] !== undefined && cleanedData[key] !== null) {
            if (typeof cleanedData[key] === 'object' && !(cleanedData[key] instanceof File)) {
                formData.append(key, JSON.stringify(cleanedData[key]));
            } else {
              formData.append(key, cleanedData[key].toString());
            }
          }
        });

        const response = await axiosInstance.post<{ data: Candidate }>(this.baseUrl, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response.data && response.data.data) {
          return response.data.data;
        }
        return response.data as any;
      } else {
        // No file upload, send as JSON
        const response = await axiosInstance.post<{ data: Candidate }>(this.baseUrl, cleanedData);

        if (response.data && response.data.data) {
          return response.data.data;
        }
        return response.data as any;
      }
    } catch (error: any) {
      throw error;
    }
  }

  // PATCH update candidate
  async updateCandidate(id: string, candidateData: UpdateCandidateRequest & { resumeFile?: File }): Promise<Candidate> {
    try {
      // Validate new fields
      this.validateCandidateData(candidateData);

      if (candidateData.status) {
        // Validation removed as per user request
      }

      // Check if we have a resume file to upload
      const { resumeFile, ...candidateDataWithoutFile } = candidateData;

      // Clean up empty strings to avoid backend validation issues
      const cleanedData: any = { ...candidateDataWithoutFile };
      // Fields that should allow being cleared/empty strings
      const clearableFields = [
        'expectedSalary',
        'currentSalary',
        'currentCompany',
        'currentRole',
        'noticePeriod',
        'linkedinUrl',
        'notes',
        'reasonForLeavingPreviousRole',
        'communicationSkills'
      ];

      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === '' && !clearableFields.includes(key)) {
          cleanedData[key] = undefined;
        }
      });

      let response: any;

      if (resumeFile) {
        // Create FormData for file upload
        const formData = new FormData();

        // Add the resume file
        formData.append('resume', resumeFile);

        // Add fields from cleanedData
        Object.keys(cleanedData).forEach(key => {
          if (cleanedData[key] !== undefined && cleanedData[key] !== null) {
            if (typeof cleanedData[key] === 'object' && !(cleanedData[key] instanceof File)) {
              formData.append(key, JSON.stringify(cleanedData[key]));
            } else {
              formData.append(key, cleanedData[key].toString());
            }
          }
        });

        response = await axiosInstance.patch<{ data: Candidate }>(`${this.baseUrl}/${id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        // No file upload, send as JSON
        response = await axiosInstance.patch(`${this.baseUrl}/${id}`, cleanedData);
      }

      let updatedCandidate: Candidate;

      // Check if response has the expected structure
      if (response.data && response.data.data) {
        updatedCandidate = response.data.data;
      } else if (response.data && typeof response.data === 'object' && (response.data as any)._id) {
        // If API returns candidate directly without wrapping in 'data' property
        updatedCandidate = response.data as Candidate;
      } else {
        // Fallback: fetch the updated candidate by ID
        updatedCandidate = await this.getCandidateById(id);
      }

      return updatedCandidate;
    } catch (error) {
      throw error;
    }
  }

  // PATCH update candidate status
  async updateCandidateStatus(id: string, status: string, notes?: string): Promise<Candidate> {
    try {
      // Get current candidate to compare status
      let currentCandidate: Candidate | null = null;
      let previousStatus: string | null = null;

      try {
        currentCandidate = await this.getCandidateById(id);
        previousStatus = currentCandidate?.status || null;
      } catch (error) {
        // Continue with status update even if we can't get current status
      }

      // Validate status transitions based on business rules
      if (currentCandidate) {
        const validationError = this.validateStatusTransition(currentCandidate, status);
        if (validationError) {
          throw new Error(validationError);
        }
      }

      // Use the regular update endpoint since there might not be a separate status endpoint
      const updateData: UpdateCandidateRequest = {
        status,
        statusNotes: notes,
        feedback: status === 'INTERVIEW_COMPLETED' ? notes : undefined
      };

      const response = await axiosInstance.patch(`${this.baseUrl}/${id}`, updateData);


      let updatedCandidate: Candidate;

      // Check if response has the expected structure
      if (response.data && response.data.data) {
        updatedCandidate = response.data.data;
      } else if (response.data && typeof response.data === 'object' && (response.data as any)._id) {
        // If API returns candidate directly without wrapping in 'data' property
        updatedCandidate = response.data as Candidate;
      } else {
        // Fallback: fetch the updated candidate by ID
        updatedCandidate = await this.getCandidateById(id);
      }

      // Dispatch event for status change to trigger interview list refresh
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('candidate-status-updated', {
          detail: {
            action: 'status_updated',
            status: status,
            category: 'candidate',
            candidateId: id,
            previousStatus: previousStatus
          }
        }));
      }

      return updatedCandidate;
    } catch (error) {
      throw error;
    }
  }

  // DELETE candidate
  async deleteCandidate(id: string): Promise<void> {
    try {
      await axiosInstance.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      throw error;
    }
  }

  // GET candidate statistics
  async getCandidateStats(): Promise<CandidateStats> {
    try {
      const response = await axiosInstance.get<{ data: CandidateStats }>(`${this.baseUrl}/stats`);
      return response.data.data;
    } catch (error) {
      throw error;
    }
  }

  // POST add timeline entry - using new timeline API
  async addTimelineEntry(candidateId: string, timelineData: {
    type: TimelineEntry['type'];
    subType: string;
    title: string;
    description: string;
    metadata?: Record<string, any>;
  }): Promise<TimelineEntry> {
    try {
      // Use the new timeline service
      return await timelineService.addCandidateTimelineEntry(candidateId, timelineData);
    } catch (error) {
      throw error;
    }
  }

  // GET timeline for candidate - using new timeline API
  async getCandidateTimeline(candidateId: string): Promise<TimelineEntry[]> {
    try {
      // Use the new timeline service
      const response = await timelineService.getCandidateTimeline(candidateId);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // POST bulk operations
  async bulkUpdateCandidates(candidateIds: string[], updateData: UpdateCandidateRequest): Promise<Candidate[]> {
    try {
      const response = await axiosInstance.post<{ data: Candidate[] }>(`${this.baseUrl}/bulk-update`, {
        candidateIds,
        updateData
      });
      return response.data.data;
    } catch (error) {
      throw error;
    }
  }

  // GET export candidates
  async exportCandidates(filters: CandidateFilters = {}): Promise<Blob> {
    try {
      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await axiosInstance.get(
        `${this.baseUrl}/export${params.toString() ? `?${params.toString()}` : ''}`,
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // POST upload resume
  async uploadResume(candidateId: string, file: File): Promise<{ resumeLink: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'resume');
      formData.append('candidateId', candidateId);

      const response = await axiosInstance.post<{ data: { resumeLink: string } }>(
        `${this.baseUrl}/${candidateId}/resume`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data.data;
    } catch (error) {
      throw error;
    }
  }

  // GET search candidates
  async searchCandidates(query: string, filters: Partial<CandidateFilters> = {}): Promise<CandidatesResponse> {
    try {
      const searchFilters = { ...filters, search: query };
      return await this.getCandidates(searchFilters);
    } catch (error) {
      throw error;
    }
  }

  // Search candidates by LinkedIn profile
  async searchByLinkedIn(linkedinUrl: string): Promise<CandidatesResponse> {
    try {
      const formattedUrl = this.formatLinkedInUrl(linkedinUrl);
      const filters: CandidateFilters = { linkedinUrl: formattedUrl };
      return await this.getCandidates(filters);
    } catch (error) {
      throw error;
    }
  }

  // GET unique creator IDs
  async getCandidateCreators(): Promise<string[]> {
    try {
      const response = await axiosInstance.get<string[]>(`${this.baseUrl}/creators`);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching candidate creators:', error);
      return [];
    }
  }


  // Get candidates by salary range
  async getCandidatesBySalaryRange(minSalary: number, maxSalary: number): Promise<CandidatesResponse> {
    try {
      const filters: CandidateFilters = {
        expectedSalaryMin: minSalary,
        expectedSalaryMax: maxSalary
      };
      return await this.getCandidates(filters);
    } catch (error) {
      throw error;
    }
  }

  // Helper method to get candidate full name
  getCandidateFullName(candidate: Candidate | undefined | null): string {
    if (!candidate || !candidate.firstName || !candidate.lastName) {
      return 'Unknown Candidate';
    }
    return `${candidate.firstName} ${candidate.lastName}`.trim();
  }

  // Helper method to get status color
  getStatusColor(status: string): string {
    const statusStr = String(status).toUpperCase();

    const statusColors: Record<string, string> = {
      [HiringStatus.NEW]: '#3B82F6',
      [HiringStatus.SHORTLISTED]: '#06B6D4',
      [HiringStatus.SELECTED_FOR_INTERVIEW]: '#8B5CF6',
      [HiringStatus.REJECTED]: '#EF4444',
      [HiringStatus.INTERVIEW_SCHEDULED]: '#6366F1',
      [HiringStatus.INTERVIEW_COMPLETED]: '#A855F7',
      [HiringStatus.CANDIDATE_SELECTED]: '#10B981',
      [HiringStatus.CANDIDATE_REJECTED]: '#F43F5E',
      [HiringStatus.OFFER_SENT]: '#F59E0B',
      [HiringStatus.OFFER_ACCEPTED]: '#22C55E',
      [HiringStatus.OFFER_REJECTED]: '#991B1B',
      [HiringStatus.HIRED]: '#15803D',
      [HiringStatus.FUTURE_REFERENCE]: '#0D9488',
      [HiringStatus.ON_HOLD]: '#D97706',
    };
    return statusColors[statusStr] || '#6B7280';
  }

  // Helper method to get status display text
  getStatusDisplayText(status: string): string {
    const statusStr = String(status).toUpperCase();

    const statusTexts: Record<string, string> = {
      [HiringStatus.NEW]: 'New',
      [HiringStatus.SHORTLISTED]: 'Shortlisted',
      [HiringStatus.SELECTED_FOR_INTERVIEW]: 'Selected for Interview',
      [HiringStatus.REJECTED]: 'Rejected for Interview',
      [HiringStatus.INTERVIEW_SCHEDULED]: 'Interview Scheduled',
      [HiringStatus.INTERVIEW_COMPLETED]: 'Interview Completed',
      [HiringStatus.CANDIDATE_SELECTED]: 'Candidate Selected',
      [HiringStatus.CANDIDATE_REJECTED]: 'Candidate Rejected',
      [HiringStatus.OFFER_SENT]: 'Offer Sent',
      [HiringStatus.OFFER_ACCEPTED]: 'Offer Accepted',
      [HiringStatus.OFFER_REJECTED]: 'Offer Rejected',
      [HiringStatus.HIRED]: 'Hired',
      [HiringStatus.FUTURE_REFERENCE]: 'Future Reference',
      [HiringStatus.ON_HOLD]: 'On Hold',
    };
    return statusTexts[statusStr] || statusStr;
  }

  // Validate candidate data including new fields
  private validateCandidateData(candidateData: CreateCandidateRequest | UpdateCandidateRequest): void {
    // Validate LinkedIn URL if provided
    if (candidateData.linkedinUrl) {
      const linkedinRegex = /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/;
      if (!linkedinRegex.test(candidateData.linkedinUrl)) {
        throw new Error('Invalid LinkedIn URL format');
      }
    }
  }

  // Helper method to format LinkedIn URL
  formatLinkedInUrl(url: string): string {
    if (!url) return '';

    // Remove any trailing slashes
    let formattedUrl = url.trim();
    if (formattedUrl.endsWith('/')) {
      formattedUrl = formattedUrl.slice(0, -1);
    }

    // Ensure it starts with https://
    if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
      formattedUrl = `https://${formattedUrl}`;
    }

    return formattedUrl;
  }



  // Helper method to get salary range display
  getSalaryRangeDisplay(candidate: Candidate): string {
    const currentSalary = (candidate as any).currentSalary;
    const expectedSalary = candidate.expectedSalary;

    if (currentSalary && expectedSalary) {
      return `${currentSalary} → ${expectedSalary}`;
    } else if (currentSalary) {
      return `Current: ${currentSalary}`;
    } else if (expectedSalary) {
      return `Expected: ${expectedSalary}`;
    }

    return 'Not specified';
  }


  // Helper method to check if candidate has moved past "New" status
  public hasMovedPastNewStatus(candidate: Candidate): boolean {
    // If candidate's current status is not "New", they have moved past it
    let currentStatusStr = '';
    const statusAny = candidate.status as any;
    if (typeof statusAny === 'object' && statusAny !== null && 'name' in statusAny) {
      currentStatusStr = statusAny.name.toLowerCase();
    } else {
      currentStatusStr = String(candidate.status).toLowerCase();
    }

    // If current status is not "new", they have moved past it
    if (currentStatusStr !== 'new') {
      return true;
    }

    // Check timeline for any status changes from "New" to other statuses
    if (!candidate.timeline || candidate.timeline.length === 0) {
      return false;
    }

    // Check if there's any timeline entry showing a status change from "New"
    const hasMovedPastNew = candidate.timeline.some(event => {
      const eventTitle = event.title?.toLowerCase() || '';
      const eventDescription = event.description?.toLowerCase() || '';
      const eventType = event.type?.toLowerCase() || '';

      // Check for status change events that mention moving from "New"
      const isStatusChangeFromNew =
        (eventTitle.includes('status') && eventDescription.includes('new')) ||
        (eventType === 'status_change' && eventDescription.includes('new')) ||
        (eventTitle.includes('new') && eventDescription.includes('status')) ||
        // Check metadata for status changes
        (event.metadata?.oldStatus?.name?.toLowerCase() === 'new') ||
        (event.metadata?.oldStatus?.toLowerCase() === 'new') ||
        // Check for any timeline entry that indicates progression beyond "New"
        (eventTitle.includes('interview') || eventTitle.includes('screening') || eventTitle.includes('offer') || eventTitle.includes('hired')) ||
        (eventDescription.includes('interview') || eventDescription.includes('screening') || eventDescription.includes('offer') || eventDescription.includes('hired'));

      return isStatusChangeFromNew;
    });

    return hasMovedPastNew;
  }

  // Validate status transitions based on business rules
  public validateStatusTransition(_candidate: Candidate, _newStatus: string): string | null {
    // Conditions removed as per user request for seamless flow
    return null; // No validation error
  }
}

export const candidateService = new CandidateService();
export default candidateService; 	